import { NoResults, Spinner } from "client/Components";
import { DataSourceSortableState } from "client/Data/sorting";
import { useForceRerender } from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { HubItemCategoryModel } from "client/Api/Hub/hub";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	hubItemIsNewToFlag
} from "../../common";
import { List } from "../Overview/List/List";
import { createFilters, createOrder } from "../Overview/useDataSource";
import {
	getOtherEvents,
	EventDetailsViewModel,
	EventPagingInformationModel,
	EventPreviewModel,
	EventDateFilterType,
	EventDateSortDirection
} from "client/Api/Hub/Events/eventViewer";
import styles from "./OtherEvents.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export type OtherEventsProps = {
	item: EventDetailsViewModel | null;
	getCategory: (categoryId: number | null) => HubItemCategoryModel | undefined;
	activeFilter: ActiveFilter | null;
	onActiveFilterChange(filter: ActiveFilter): void;
	selectedCategoryId: number | null;
	onSelectedCategoryIdChange(categoryId: number): void;
	searchTerm: VersionedValue<string>;
	sorting: DataSourceSortableState<EventPreviewModel>;
	dateFilter: EventDateFilterType;
	onNavigateToItem(id: number): void;
	timezone: TimeZone;
	isNewFilter: HubItemIsNewFilter;
};

export const OtherEvents = (props: OtherEventsProps): ReactElement | null => {
	const {
		item,
		getCategory,
		activeFilter,
		onActiveFilterChange,
		selectedCategoryId,
		onSelectedCategoryIdChange,
		searchTerm,
		sorting,
		dateFilter,
		onNavigateToItem,
		timezone,
		isNewFilter
	} = props;

	const [state, setState] = useState<LoadingState>({
		kind: "loading"
	});

	const [retryHandler, itemsVersion] = useForceRerender();

	useEffect(
		(): (() => void) | undefined => {
			if (!item) {
				return;
			}

			hasImplicitDependencyOn(itemsVersion);

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setState({
						kind: "loading"
					});

					const {
						id,
						startDate
					} = item;

					const pagingInformation: EventPagingInformationModel = {
						id,
						startDate
					};

					const [authorId, tagId] = createFilters(activeFilter);

					const sortDirection: EventDateSortDirection | null = createOrder(sorting);

					const items: readonly EventPreviewModel[] = await getOtherEvents(
						{
							id,
							pagingInformation,
							categoryId: selectedCategoryId,
							authorId,
							tagId,
							searchTerm: searchTerm.value,
							filterType: dateFilter,
							sortDirection,
							isNew: hubItemIsNewToFlag[isNewFilter]
						},
						signal
					);

					setState({
						kind: "success",
						items
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);

					setState({
						kind: "error"
					});
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			activeFilter,
			dateFilter,
			item,
			itemsVersion,
			searchTerm.value,
			selectedCategoryId,
			sorting,
			isNewFilter
		]
	);

	switch (state.kind)
	{
		case "loading":
			return (
				<div className={styles.spinnerBlock}>
					<Spinner />
				</div>
			);
		case "error":
			return (
				<NoResults
					trackingId="relevant-items-failure"
					icon="calendar-star"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={retryHandler}
				/>
			);
		case "success":
			if (state.items.length === 0) {
				return (
					<NoResults
						trackingId="no-relevant-items"
						icon="calendar-star"
						title={localize("_NoItemsToShow")}
						message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
					/>
				);
			}

			return (
				<List
					items={state.items}
					kind="relevant"
					onNavigateToItem={onNavigateToItem}
					getCategory={getCategory}
					onActiveFilterChange={onActiveFilterChange}
					onSelectedCategoryIdChange={onSelectedCategoryIdChange}
					timezone={timezone}
				/>
			);
		default:
			never(state);
	}
};
OtherEvents.displayName = "OtherEvents";

type LoadingState =
	| { kind: "loading"; }
	| { kind: "error"; }
	| { kind: "success"; items: readonly EventPreviewModel[]; };
